// Hand-mirrored from the BE Pydantic schemas in
// services/shared/shared_layer/schemas/. Each block exports both the raw
// enum values AND a pre-built `*_OPTIONS` array (for <Field type="select"/>),
// so consumers never have to rebuild option arrays in their components.
//
// When BE enums change, update this file. Codegen (openapi-typescript or
// similar) can replace this later — keep names BE-identical so a generator
// can drop in without churn.
//
// Convention:
//   FOO              — { KEY: "VALUE", ... } object (the enum itself)
//   FOO_OPTIONS      — [{ label, value }, ...] derived from FOO

// ─── helpers ─────────────────────────────────────────────────────────────

const titleCase = (s) =>
  String(s)
    .toLowerCase()
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// Build option array from an enum-like object. Pass `labelMap` to override
// individual labels (otherwise values are title-cased from the keys).
const toOptions = (obj, labelMap = {}) =>
  Object.entries(obj).map(([key, value]) => ({
    value,
    label: labelMap[key] ?? labelMap[value] ?? titleCase(key),
  }));

// ─── Customer ────────────────────────────────────────────────────────────

export const CUSTOMER_TYPE = {
  AGENT: "AGENT",
  DEVELOPER: "DEVELOPER",
  ZUMALE_USER: "ZUMALE_USER",
  // Work-marketplace personas (backend v3_67). Absent here, any UI driven by
  // this map silently omitted them.
  ARCHITECT: "ARCHITECT",
  FREELANCER: "FREELANCER",
};
export const CUSTOMER_TYPE_OPTIONS = toOptions(CUSTOMER_TYPE, {
  AGENT: "Channel Partner",
  DEVELOPER: "Developer",
  ZUMALE_USER: "Zumale User",
  ARCHITECT: "Architect",
  FREELANCER: "Freelancer",
});

export const PERSONA_TYPE = {
  STAFF: "STAFF",
  DEVELOPER_ADMIN: "DEVELOPER_ADMIN",
  CHANNEL_PARTNER: "CHANNEL_PARTNER",
  PROPERTY_BUYER: "PROPERTY_BUYER",
  INTERIOR_DESIGNER: "INTERIOR_DESIGNER",
  BV_ASSOCIATE: "BV_ASSOCIATE",
  ZUMALE_USER: "ZUMALE_USER",
};
export const PERSONA_TYPE_OPTIONS = toOptions(PERSONA_TYPE, {
  STAFF: "Staff",
  DEVELOPER_ADMIN: "Developer Admin",
  CHANNEL_PARTNER: "Channel Partner",
  PROPERTY_BUYER: "Property Buyer",
  INTERIOR_DESIGNER: "Interior Designer",
  BV_ASSOCIATE: "BV Associate",
  ZUMALE_USER: "Zumale User",
});

export const CUSTOMER_USER_STATUS = {
  OPEN: "OPEN",
  CLOSED: "CLOSED",
};
export const CUSTOMER_USER_STATUS_OPTIONS = toOptions(CUSTOMER_USER_STATUS);

export const CUSTOMER_LEAD_STATUS = {
  NO_INTENT: "NO_INTENT",
  LOW_INTENT: "LOW_INTENT",
  HIGH_INTENT: "HIGH_INTENT",
  NEGOTIATION: "NEGOTIATION",
};
export const CUSTOMER_LEAD_STATUS_OPTIONS = toOptions(CUSTOMER_LEAD_STATUS);

export const PERSONA = {
  COLD_LEAD: "COLD_LEAD",
  LUKE_WARM_LEAD: "LUKE_WARM_LEAD",
  WARM_LEAD: "WARM_LEAD",
  HOT_LEAD: "HOT_LEAD",
  CUSTOMER: "CUSTOMER",
  AGENT: "AGENT",
};
export const PERSONA_OPTIONS = toOptions(PERSONA);

// ─── RBAC ────────────────────────────────────────────────────────────────

export const PERMISSION_GROUP = {
  MANAGE_PROJECTS: "MANAGE_PROJECTS",
  MANAGE_PRESALES: "MANAGE_PRESALES",
  MANAGE_SALES: "MANAGE_SALES",
  MANAGE_CHANNEL_PARTNERS: "MANAGE_CHANNEL_PARTNERS",
  CROSS_CUTTING: "CROSS_CUTTING",
};

export const PERMISSION = {
  PROJECT_CREATE: "project.create",
  PROJECT_EDIT: "project.edit",
  PROPERTY_EDIT: "property.edit",
  PROPERTY_DELETE: "property.delete",
  PROJECT_CHANNEL_PARTNERS_READ: "project.channel_partners.read",
  PROJECT_PROPERTIES_READ: "project.properties.read",
  LEAD_CREATE: "lead.create",
  LEAD_EDIT: "lead.edit",
  LEAD_DELETE: "lead.delete",
  SALES_AGENT_MANAGE: "sales_agent.manage",
  SALE_CREATE: "sale.create",
  SALE_READ: "sale.read",
  SALE_EDIT: "sale.edit",
  CUSTOMER_READ: "customer.read",
  CHANNEL_PARTNER_CREATE: "channel_partner.create",
  CHANNEL_PARTNER_EDIT: "channel_partner.edit",
  CHANNEL_PARTNER_DELETE: "channel_partner.delete",
  PROJECT_READ: "project.read",
  PROPERTY_READ: "property.read",
  ROLE_MANAGE: "role.manage",
};

export const PREDEFINED_ROLE_CODE = {
  ADMIN: "ADMIN",
  SALES: "SALES",
  PRESALES: "PRESALES",
  BV_ASSOCIATE: "BV_ASSOCIATE",
};
export const PREDEFINED_ROLE_CODE_OPTIONS = toOptions(PREDEFINED_ROLE_CODE);

// ─── Property ────────────────────────────────────────────────────────────

export const PROPERTY_TYPE = {
  APARTMENT: "Apartment",
  SHOP: "Shop",
  LAND: "Land",
  INDEPENDENT: "Independent",
};
export const PROPERTY_TYPE_OPTIONS = toOptions(PROPERTY_TYPE);

export const PROPERTY_SALE_TYPE = {
  NEW_SALE: "NewSale",
  RESALE: "Resale",
  RENT: "Rent",
};
export const PROPERTY_SALE_TYPE_OPTIONS = toOptions(PROPERTY_SALE_TYPE, {
  NEW_SALE: "New Sale",
  RESALE: "Resale",
  RENT: "Rent",
});

export const PROPERTY_STATE = {
  UNDER_CONSTRUCTION: "Under Construction",
  READY: "Ready",
};
export const PROPERTY_STATE_OPTIONS = toOptions(PROPERTY_STATE);

export const PROPERTY_STATUS = {
  SOLD: "Sold",
  UNSOLD: "Unsold",
};
export const PROPERTY_STATUS_OPTIONS = toOptions(PROPERTY_STATUS);

// ─── Sales ───────────────────────────────────────────────────────────────

export const CUSTOMER_SALES_PAYMENT_TYPE = {
  TOKEN_PAYMENT: 1,
  SCHEDULED_PAYMENT: 2,
  FULL_PAYMENT: 3,
  DOWN_PAYMENT: 4,
  SLAB_WISE_PAYMENT: 5,
};
export const CUSTOMER_SALES_PAYMENT_TYPE_OPTIONS = [
  { value: 1, label: "Token Payment" },
  { value: 2, label: "Scheduled Payment" },
  { value: 3, label: "Full Payment" },
  { value: 4, label: "Down Payment" },
  { value: 5, label: "Slab-Wise Payment" },
];

export const CUSTOMER_SALES_PAYMENT_OPTION = {
  SLAB_WISE: "SLAB_WISE",
  SCHEDULED: "SCHEDULED",
  HYBRID: "HYBRID",
};
export const CUSTOMER_SALES_PAYMENT_OPTION_OPTIONS = toOptions(CUSTOMER_SALES_PAYMENT_OPTION);

export const CUSTOMER_SALES_SOURCE = {
  CHANNEL_PARTNER: "CHANNEL_PARTNER",
  PRE_SALES: "PRE_SALES",
  REFERRAL: "REFERRAL",
  HOARDING: "HOARDING",
  META: "META",
  GOOGLE: "GOOGLE",
  YOUTUBE: "YOUTUBE",
  PROPERTY_PORTALS: "PROPERTY_PORTALS",
};
export const CUSTOMER_SALES_SOURCE_OPTIONS = toOptions(CUSTOMER_SALES_SOURCE);

export const CUSTOMER_SALES_CANCELLATION_STATUS = {
  INITIATED: "Initiated",
  COMPLETED: "Completed",
};
export const CUSTOMER_SALES_CANCELLATION_STATUS_OPTIONS = toOptions(CUSTOMER_SALES_CANCELLATION_STATUS);

export const CUSTOMER_SALES_ID_STATUS = {
  ID_REQUIRED: "ID_REQUIRED",
  ID_DISCOVERY: "ID_DISCOVERY",
  ID_SHORTLISTING_IN_PROGRESS: "ID_SHORTLISTING_IN_PROGRESS",
  ID_AI_CONCEPT_IN_PROGRESS: "ID_AI_CONCEPT_IN_PROGRESS",
  ID_SAMPLE_REQUESTED: "ID_SAMPLE_REQUESTED",
  ID_SAMPLE_DESIGN_SHORTLISTING_IN_PROGRESS: "ID_SAMPLE_DESIGN_SHORTLISTING_IN_PROGRESS",
  ID_FINALIZED: "ID_FINALIZED",
  ID_FULL_DESIGN_REQUESTED: "ID_FULL_DESIGN_REQUESTED",
  ID_FULL_DESIGN_IN_PROGRESS: "ID_FULL_DESIGN_IN_PROGRESS",
  ID_FULL_DESIGN_SUBMITTED: "ID_FULL_DESIGN_SUBMITTED",
  ID_FULL_DESIGN_FINALIZED: "ID_FULL_DESIGN_FINALIZED",
  ID_FULL_DESIGN_MOD_REQUESTED: "ID_FULL_DESIGN_MOD_REQUESTED",
  ID_FULL_DESIGN_APPROVAL_PENDING: "ID_FULL_DESIGN_APPROVAL_PENDING",
  ID_FULL_DESIGN_APPROVED: "ID_FULL_DESIGN_APPROVED",
  ID_EXECUTION_READY: "ID_EXECUTION_READY",
  ID_ON_HOLD: "ID_ON_HOLD",
  ID_DROPPED: "ID_DROPPED",
  ID_ESCALATION_REQUIRED: "ID_ESCALATION_REQUIRED",
};
export const CUSTOMER_SALES_ID_STATUS_OPTIONS = toOptions(CUSTOMER_SALES_ID_STATUS);

// ─── Documents ───────────────────────────────────────────────────────────

export const CUSTOMER_DOCUMENT_TYPE = {
  BOOKING_LETTER: "BOOKING_LETTER",
  CANCELLATION_LETTER: "CANCELLATION_LETTER",
  PROPERTY_HANDOVER_LETTER: "PROPERTY_HANDOVER_LETTER",
  COST_SHEET_NOTIFICATION: "COST_SHEET_NOTIFICATION",
  CANCELLATION_REFUND: "CANCELLATION_REFUND",
  TOKEN_PAYMENT: "TOKEN_PAYMENT",
  DOWN_PAYMENT: "DOWN_PAYMENT",
  SCHEDULED_PAYMENT: "SCHEDULED_PAYMENT",
  FULL_PAYMENT: "FULL_PAYMENT",
  SLAB_WISE_PAYMENT: "SLAB_WISE_PAYMENT",
  SCHEDULED_DEMAND_LETTERS_USER: "SCHEDULED_DEMAND_LETTERS_USER",
  SCHEDULED_DEMAND_LETTERS_BANK: "SCHEDULED_DEMAND_LETTERS_BANK",
  SCHEDULED_DEMAND_LETTERS_POST: "SCHEDULED_DEMAND_LETTERS_POST",
  SLAB_WISE_DEMAND_LETTERS_POST: "SLAB_WISE_DEMAND_LETTERS_POST",
  SLAB_WISE_DEMAND_LETTERS_USER: "SLAB_WISE_DEMAND_LETTERS_USER",
  SLAB_WISE_DEMAND_LETTERS_BANK: "SLAB_WISE_DEMAND_LETTERS_BANK",
};
export const CUSTOMER_DOCUMENT_TYPE_OPTIONS = toOptions(CUSTOMER_DOCUMENT_TYPE);

// ─── WhatsApp ────────────────────────────────────────────────────────────

export const WHATSAPP_TEMPLATE_STATUS = {
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  PENDING: "PENDING",
};
export const WHATSAPP_TEMPLATE_STATUS_OPTIONS = toOptions(WHATSAPP_TEMPLATE_STATUS);

export const WHATSAPP_TEMPLATE_TYPE = {
  BIRTHDAY_TEMPLATE: "BIRTHDAY_TEMPLATE",
  BIRTHDAY_OFFER_TEMPLATE: "BIRTHDAY_OFFER_TEMPLATE",
  FESTIVAL_TEMPLATE: "FESTIVAL_TEMPLATE",
  FESTIVAL_OFFER_TEMPLATE: "FESTIVAL_OFFER_TEMPLATE",
  CAMPAIGN_CONDITIONAL_TEMPLATE: "CAMPAIGN_CONDITIONAL_TEMPLATE",
  POST_VISIT_TEMPLATE: "POST_VISIT_TEMPLATE",
  BUILDVIZER_TEMPLATE: "BUILDVIZER_TEMPLATE",
  STANDARD_TEMPLATE: "STANDARD_TEMPLATE",
  REPLY_NODE_TEMPLATE: "REPLY_NODE_TEMPLATE",
  SITE_VISIT_TEMPLATE: "SITE_VISIT_TEMPLATE",
};
export const WHATSAPP_TEMPLATE_TYPE_OPTIONS = toOptions(WHATSAPP_TEMPLATE_TYPE);

// ─── Approval ────────────────────────────────────────────────────────────

export const APPROVAL_STATUS = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
};
export const APPROVAL_STATUS_OPTIONS = toOptions(APPROVAL_STATUS);

// ─── Interior Designer ───────────────────────────────────────────────────

export const DESIGN_ITERATION_STATUS = {
  SUBMITTED: "SUBMITTED",
  FEEDBACK_PROVIDED: "FEEDBACK_PROVIDED",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
};
export const DESIGN_ITERATION_STATUS_OPTIONS = toOptions(DESIGN_ITERATION_STATUS);

// ─── Device ──────────────────────────────────────────────────────────────

export const DEVICE_PLATFORM = {
  ANDROID: "ANDROID",
  IOS: "IOS",
  WEB: "WEB",
};
export const DEVICE_PLATFORM_OPTIONS = toOptions(DEVICE_PLATFORM);

// ─── Common India dropdowns (frequently needed) ──────────────────────────

export const INDIAN_STATE_OPTIONS = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
].map((s) => ({ label: s, value: s }));
